/**
 * Wish binders and master sets, stored as binders rows.
 *
 * The binders table already had a binder_type column that no code read, so
 * rather than standing up parallel tables a wish binder and a master set are
 * binders rows with a different type. Everything hanging off binders keeps
 * working untouched: the database-minted share token, the definer reader
 * granted to anon, the public /b/:token route, the native share sheet.
 *
 * The cost of that choice is that every screen listing a person's binders will
 * happily list their wish binders and their master sets too. That is what
 * PC_ONLY is for, and it is applied at every binder list query in the app. It
 * changes nothing today, because every existing row is 'pc'; it is here so the
 * first wish binder does not appear in the collection.
 */

import { supabase } from '../lib/supabase';

export const BINDER_TYPE_PC = 'pc';
export const BINDER_TYPE_WISH = 'wish';
export const BINDER_TYPE_MASTER = 'master';

/**
 * Restrict a binders query to real binders of owned cards.
 *
 * @example supabase.from('binders').select('*').eq('user_id', id).match(PC_ONLY)
 */
export const PC_ONLY = { binder_type: BINDER_TYPE_PC };

/**
 * A master set or a sub set. One type for both, because a sub set is the same
 * row with a scope over the master's slots rather than a set of its own.
 *
 * scope is null for the whole master. Otherwise it carries either a printing
 * filter, a card range filter, or both:
 *
 *   { printing: { subtype: 'shadowless', stamps: ['1st-edition'] } }
 *   { range: { prefix: 'TG' } }
 *   { range: { prefix: '', from: 173, to: 186 } }
 *
 * Printings multiply the slot count - Base Set is 102 cards across 4 printings,
 * so 408 slots. Card ranges do not: their cards are already inside the set, so
 * the Trainer Gallery is 30 of Brilliant Stars' 216 rather than a parallel 216.
 */
export async function fetchMasterSets(userId) {
  const { data, error } = await supabase
    .from('binders')
    .select('*')
    .eq('user_id', userId)
    .eq('binder_type', BINDER_TYPE_MASTER)
    .order('name');
  if (error) throw error;
  return data || [];
}

export async function fetchWishBinders(userId) {
  const { data, error } = await supabase
    .from('binders')
    .select('*')
    .eq('user_id', userId)
    .eq('binder_type', BINDER_TYPE_WISH)
    .order('name');
  if (error) throw error;
  return data || [];
}

/** The cards a wish binder holds. These are wanted, so they are not in cards. */
export async function fetchWishBinderCards(binderId) {
  const { data, error } = await supabase
    .from('wish_binder_cards')
    .select('*')
    .eq('binder_id', binderId)
    .order('position', { ascending: true, nullsFirst: false })
    .order('added_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

/** How many slots a master set or sub set has, and how many are filled. */
export async function fetchSetProgress(setId, scope, profileId) {
  const { data, error } = await supabase.rpc('wish_set_progress', {
    p_set_id: setId,
    p_scope: scope || null,
    ...(profileId ? { p_as: profileId } : {}),
  });
  if (error) throw error;
  return data;
}

/**
 * The sub sets a set actually has, derived from the catalogue rather than
 * stored. A printing only qualifies when it covers every card in the set,
 * which is what a print run is; otherwise 151 would offer a one-slot GameStop
 * "sub set".
 */
export async function fetchSetSubsets(setId) {
  const { data, error } = await supabase.rpc('wish_set_subsets', { p_set_id: setId });
  if (error) throw error;
  return data || [];
}

/** Total slots for a set or a scope, without asking about anyone's progress. */
export async function fetchSetSlots(setId, scope) {
  const { data, error } = await supabase.rpc('wish_set_slots', {
    p_set_id: setId,
    p_scope: scope || null,
  });
  if (error) throw error;
  return Number(data) || 0;
}

/**
 * The Postgres codes a binder write can come back with that are not faults.
 *
 * Binder names are unique per user PER KIND, enforced by
 * unique_binder_name_per_user_type. So a PC binder and a chase binder MAY share
 * a name - they are different objects - but two chase binders may not. The old
 * index was (user_id, name) and did not know about binder_type, which meant
 * chasing the Base Set master set permanently blocked naming a collection
 * binder "Base Set"; widened 09.12.2026.
 *
 * The regex below still matches, because the new index name contains the old
 * one as a substring. Do not tighten it to an exact match without checking that.
 */
const UNIQUE_VIOLATION = '23505';

export function isDuplicateBinderNameError(error) {
  if (!error) return false;
  if (error.code === UNIQUE_VIOLATION) return true;
  return /unique_binder_name_per_user|duplicate key/i.test(error.message || '');
}

/** What to say when the name is taken. Says "chase binder" rather than
 * "binder", because a PC binder of the same name is no longer a collision and
 * telling someone a name is taken when they cannot see the thing taking it is
 * the confusing half of this error. */
export function duplicateBinderNameMessage(name) {
  return `You already have a chase binder called "${name}". Pick another name.`;
}

/**
 * A chase binder: a wish list of cards the person does not own, invisible to
 * matching and invisible to every screen that lists binders of owned cards.
 *
 * Named before the row exists, the same way a PC binder is, which is why the
 * name and the colour arrive together rather than a blank row appearing first.
 */
export async function createWishBinder(userId, { name, image = 'blue', isPrivate = false }) {
  const { data, error } = await supabase
    .from('binders')
    .insert({
      user_id: userId,
      name,
      image,
      is_private: !!isPrivate,
      binder_type: BINDER_TYPE_WISH,
      card_count: 0,
      last_updated: new Date().toISOString(),
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * A master set or a sub set, as a binders row.
 *
 * The name carries the scope in words - "Base Set Shadowless", never
 * "Base Set Part 2" - because that is the vocabulary a kid already reads on
 * eBay and at a card shop, so the app teaches it instead of inventing a
 * private code.
 */
export async function createMasterSet(userId, { setId, name, scope = null, image = 'blue' }) {
  const { data, error } = await supabase
    .from('binders')
    .insert({
      user_id: userId,
      name,
      image,
      is_private: false,
      binder_type: BINDER_TYPE_MASTER,
      set_id: setId,
      scope: scope || null,
      card_count: 0,
      last_updated: new Date().toISOString(),
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Stop chasing a master set, a sub set or a chase binder.
 *
 * Never touches a card the person owns. Chase's own distinction: these are
 * wish lists they simply are not chasing right now, so removing one is
 * removing an intention, not a collection.
 */
export async function removeWishSurface(binderId) {
  const { error } = await supabase.from('binders').delete().eq('id', binderId);
  if (error) throw error;
}

/** Rename a chase binder. Surfaces the name clash the same way creating does. */
export async function renameWishBinder(binderId, name) {
  const { error } = await supabase
    .from('binders')
    .update({ name, last_updated: new Date().toISOString() })
    .eq('id', binderId);
  if (error) throw error;
}

/**
 * Put a pile of catalogue cards into a chase binder.
 *
 * The picker lets somebody comb the catalogue for a while, so by the time they
 * confirm, some of what they picked may already be in the binder - they scrolled
 * past it twice, or they added it last week. The unique index
 * (binder_id, tcgdex_card_id, variant) would refuse the whole insert for one
 * repeat, and a bulk insert is all-or-nothing, so the repeats are read first and
 * dropped rather than sent and rejected. What is already there is reported back
 * instead of being treated as a failure, because it is not one: the card the
 * person wanted in the binder is in the binder.
 *
 * Positions continue from the end of the binder rather than restarting, so the
 * order somebody added things in survives, and the cards from one confirm land
 * in the order they were picked.
 *
 * @param {string} userId
 * @param {string} binderId
 * @param {object[]} cards catalogue rows: { id, name, set_id, set_name, image_url }
 * @returns {Promise<{ added: number, already: number }>}
 */
export async function addCardsToWishBinder(userId, binderId, cards) {
  const existing = await fetchWishBinderCards(binderId);
  const have = new Set(existing.map((r) => r.tcgdex_card_id));
  const fresh = (cards || []).filter((c) => c && c.id && !have.has(c.id));
  const already = (cards || []).length - fresh.length;
  if (fresh.length === 0) return { added: 0, already };

  const highest = existing.reduce((n, r) => Math.max(n, Number(r.position) || 0), 0);
  const rows = fresh.map((c, i) => ({
    binder_id: binderId,
    user_id: userId,
    tcgdex_card_id: c.id,
    card_name: c.name || null,
    set_id: c.set_id || null,
    set_name: c.set_name || null,
    // The picker offers no variant choice, and the unique index counts variant,
    // so every row it writes has to agree on one value or the same card could
    // be added twice under two spellings of "no choice made".
    variant: 'Normal',
    image_url: c.image_url || null,
    position: highest + i + 1,
  }));

  const { error } = await supabase.from('wish_binder_cards').insert(rows);
  if (error) throw error;

  await supabase
    .from('binders')
    .update({ last_updated: new Date().toISOString() })
    .eq('id', binderId);

  return { added: fresh.length, already };
}

/** How many cards sit in each of a person's chase binders. */
export async function countWishBinderCards(userId) {
  const { data, error } = await supabase
    .from('wish_binder_cards')
    .select('binder_id')
    .eq('user_id', userId);
  if (error) throw error;
  const counts = {};
  for (const row of data || []) counts[row.binder_id] = (counts[row.binder_id] || 0) + 1;
  return counts;
}
